import { useEffect, useState } from "react";
import { RouterProvider } from "react-router";

import { ConfigProvider, theme, notification } from "antd";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { getCSSByPalette } from "./styles/skin";

import { router } from "./pages/router";
import { useInitTranslations } from "utils/hooks/useSalesNetworks";


import {
    //addNotification,
    clearNotifications,
} from './store/componentsSlice';
import { SvgInfo, SvgSuccessWhiteTransparent } from "components/svg";
import numeral from "numeral";
import Loader from "components/shared/loader";

function App() {
    const skinConfig = useAppSelector((state) => state.components.skinConfig);
    const mode = useAppSelector((state) => state.components.mode);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const { init } = useInitTranslations();
    const [notificationApi, contextHolder] = notification.useNotification();
    const notificationData = useAppSelector((state) => state.components.notification);
    const loaderData = useAppSelector((state) => state.components.loader);
    const dispatch = useAppDispatch();


    useEffect(() => {
        init();
        numeral.options.currentLocale = "fr";
        numeral.register('locale', 'fr', {
            delimiters: {
                thousands: ' ',
                decimal: ','
            },
            abbreviations: {
                thousand: 'k',
                million: 'm',
                billion: 'b',
                trillion: 't'
            },
            ordinal : function (number) {
                return number === 1 ? 'er' : 'e';
            },
            currency: {
                symbol: '€'
            }
        });
    }, []);

    useEffect(() => {
        if (mode === "auto") {
            const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
            setIsDarkMode(mediaQuery.matches);

            const handleChange = (event: MediaQueryListEvent) => {
                setIsDarkMode(event.matches);
            };

            mediaQuery.addEventListener("change", handleChange);

            return () => {
                mediaQuery.removeEventListener("change", handleChange);
            };
        } else {
            setIsDarkMode(mode === "dark");
        }
    }, [mode]);

    useEffect(() => {
        document.documentElement.classList.toggle("dark-mode", isDarkMode);
    }, [isDarkMode]);

    useEffect(() => {

        if (notificationData.length) {
            notificationData.forEach((n) => {
                const { type, message, description, duration } = n;
                //notificationApi[type]({ message, description, duration });
                notificationApi.open({
                    closeIcon: false,
                    duration: duration || 2,
                    // @ts-ignore
                    placement: "center",
                    className: type,
                    message: (
                        <group
                            data-direction="row"
                            data-wrap="no"
                            data-gap="15"
                            data-space="10"
                            data-align="center"
                            data-text-align="left"
                            data-color={type === "success" ? "white" : "error-color"}
                        >
                            <icon data-svg-size="20" data-length="20" data-radius="30"
                                data-svg-color={type === "success" ? "white" : "error-color"}
                            >{type === "success" ? <SvgSuccessWhiteTransparent /> : <SvgInfo />}
                            </icon>
                            <group data-direction="column" data-gap="5">
                                <text
                                    data-weight="600"
                                    data-text-size="medium"
                                     data-wrap="wrap"
                                >{message ?? description}</text>
                            </group>
                        </group>
                    ),
                });
            });
            dispatch(clearNotifications());
        }
    }, [notificationData]); // eslint-disable-line

    const {
        sizeConfig: { fontSize, fontFamily, rootVariables },
        colorPalette,
    } = skinConfig;

    const algorithm = isDarkMode ? theme.darkAlgorithm : undefined;

    return (
        <>
            <style>
                {getCSSByPalette(colorPalette, fontFamily, fontSize as any, rootVariables)}
            </style>

            <ConfigProvider
                theme={{
                    algorithm,
                    cssVar: true,
                    token: {
                        colorLink: colorPalette.colorPrimary,
                        colorLinkActive: colorPalette.colorDark,
                        colorLinkHover: colorPalette.colorLight,
                        motionDurationMid: "0s",
                        motionDurationSlow: "0s",
                        motionDurationFast: "0s",
                        motion: false,
                        //  borderRadiusLG: 5,

                        //  borderRadiusOuter: 5,
                        lineHeight: 1.2,
                        borderRadius: 3,
                        colorPrimary: colorPalette.colorPrimary,
                        sizeStep: 3,
                        sizeUnit: 3,
                        fontSize,
                        fontFamily,
                        fontSizeLG: fontSize,
                        paddingXXS: 3,
                        //    paddingSM:3,
                        marginXXS: 3,
                        boxShadow:
                            "0 0 15px 2px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05)",
                        boxShadowSecondary:
                            "0 0 15px 2px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05)",
                    },
                    components: {
                        Notification: {
                            motionDurationMid: ".125s",
                            motionDurationSlow: ".125s",
                            motionDurationFast: ".125s",
                            borderRadiusLG: 10,
                        },
                        Message: {
                            contentPadding: 5,
                        },
                        Select: {
                            controlHeightLG: 42,
                            // paddingSM: 10,
                            paddingXS: 5,
                            paddingSM: 16,
                            singleItemHeightLG: 42,
                            controlPaddingHorizontalSM: 5,
                            controlPaddingHorizontal: 10,
                        },
                        InputNumber: {
                            controlHeightLG: 50,
                            paddingSM: 20,
                            paddingInlineLG: 15,
                        }
                    },
                }}
            >
                <>
                    {contextHolder}
                    {loaderData && loaderData === true && <Loader />}
                    <RouterProvider router={router} />
                </>
            </ConfigProvider>
        </>
    );
}

export default App;
